"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { ArrowRight, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Field, FieldGroup } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import Link from "next/link";

export default function Home() {
  const router = useRouter();

  const [search, setSearch] = useState("");
  const [isPending, startTransition] = useTransition();

  function handleSearch(event: React.FormEvent) {
    event.preventDefault();

    const query = search.trim();

    startTransition(() => {
      router.push(
        query
          ? `/clubs?q=${encodeURIComponent(query)}`
          : "/clubs"
      );
    });
  }

  return (
    <main
      className="
        relative flex flex-1 flex-col
        items-center overflow-hidden
        px-4 py-16
        sm:px-6 sm:py-20
        md:py-24
      "
    >
      {/* Background */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="
            absolute left-1/2 top-10
            h-[460px] w-[760px]
            -translate-x-1/2
            rounded-full
            bg-[#9D2235]/7
            blur-3xl
          "
        />

        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "radial-gradient(circle, #9D2235 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />

        <div
          className="
            absolute inset-0
            bg-[radial-gradient(circle_at_center,transparent_18%,hsl(var(--background))_82%)]
          "
        />
      </div>

      {/* Hero */}
      <section className="flex w-full max-w-4xl flex-col items-center text-center">
        {/* Mobile wordmark */}
        <Image
          src="/wordmark.png"
          alt="Clubs @ Temple"
          width={260}
          height={104}
          priority
          className="
            mb-7
            block h-auto
            w-[215px]
            object-contain
            sm:w-[245px]
            md:hidden
          "
        />

        {/* Desktop logo */}
        <Image
          src="/logo.png"
          alt="Clubs @ Temple"
          width={150}
          height={150}
          priority
          className="
            mb-7
            hidden h-auto
            w-[120px]
            object-contain
            drop-shadow-sm
            md:block
          "
        />

        <p
          className="
            mb-3
            hidden
            text-sm font-medium
            text-[#9D2235]
            md:block
          "
        >
          Clubs @ Temple
        </p>

        <h1
          className="
            hidden max-w-3xl
            text-4xl font-bold
            tracking-tight
            md:block
            md:text-5xl
            lg:text-6xl
          "
        >
          Discover Your{" "}
          <span className="text-[#9D2235]">
            Community
          </span>
        </h1>

        <p
          className="
            mt-3
            max-w-xl
            text-base
            leading-relaxed
            text-muted-foreground
            sm:text-lg
            md:mt-4
          "
        >
          Where students and clubs find each other.
        </p>

        {/* Search */}
        <div className="mt-9 w-full max-w-2xl">
          <form
            onSubmit={handleSearch}
            className="w-full"
          >
            <FieldGroup className="gap-2">
              <Field
                orientation="horizontal"
                className="
                  flex-col gap-2
                  sm:flex-row
                "
              >
                <div className="relative flex-1">
                  <Search
                    className="
                      pointer-events-none
                      absolute left-5 top-1/2
                      h-5 w-5
                      -translate-y-1/2
                      text-muted-foreground
                    "
                  />

                  <Input
                    type="search"
                    value={search}
                    onChange={(event) =>
                      setSearch(event.target.value)
                    }
                    placeholder="What are you interested in?"
                    className="
                      h-14
                      rounded-full
                      border-0
                      bg-muted/60
                      pl-14 pr-5
                      text-base
                      shadow-sm
                      ring-0
                      transition-all
                      focus-visible:ring-2
                      focus-visible:ring-[#9D2235]/25
                    "
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isPending}
                  className="
                    h-14
                    rounded-full
                    px-8
                    text-base
                    transition-all
                    sm:w-auto
                  "
                >
                  {isPending
                    ? "Searching..."
                    : "Search"}
                </Button>
              </Field>
            </FieldGroup>
          </form>
          
          <p
  className="
    mt-4
    text-center
    text-xs
    text-muted-foreground/75
    sm:text-sm
  "
>
  Try “medicine”, “business”, “volunteering”,
  or anything you&apos;re into.
</p>

<div className="mt-4 flex justify-center">
  <Button
    variant="ghost"
    className="
      group h-10
      rounded-full
      bg-[#9D2235]/5
      px-5
      text-sm font-medium
      text-muted-foreground
      transition-all
      hover:bg-[#9D2235]/10
      hover:text-foreground
    "
  >
    <Link
      href="/quiz"
      className="
        flex items-center
        whitespace-nowrap
      "
    >
      <span>
        Not sure where to start?
      </span>

      <span className="ml-1.5 text-[#9D2235]">
        Find your match
      </span>

      <ArrowRight
        className="
          ml-1.5 h-3.5 w-3.5
          shrink-0
          text-[#9D2235]
          transition-transform
          group-hover:translate-x-0.5
        "
      />
    </Link>
  </Button>
</div>
        </div>
        
      </section>
    </main>
  );
}